import { getAuthToken } from "@dynamic-labs/sdk-react-core";
import { removeSession, showAlert } from "../utils";
import { apiErrorCode, $axios, API_URL, $axios1 } from "./axios.instance";
import { Paths } from "./paths";

const StatusCode = apiErrorCode;

/**
 * Checks if the user validation has failed based on the response status code.
 *
 * @param data - The response data from the API.
 * @returns boolean - True if the user session is expired, token is invalid, or account is blocked, otherwise false.
 */
export const checkUserValidation = (data: any) => {
  if (data && data.statusCode) {
    const { statusCode: stc } = data;
    return (
      stc === StatusCode.SESSION_EXPIRED ||
      stc === StatusCode.INVALID_TOKEN ||
      stc === StatusCode.ACCOUNT_BLOCKED ||
      stc === StatusCode.NOT_AUTHORIZED
    );
  }
  return false;
};

/** check token */
const checkToken = () => {
  let token = getAuthToken();
  if (!token) {
    window.location.href = "/";
    return;
  }
};

/**
 * Common function to handle API calls.
 *
 * @param method - The HTTP method (GET, POST, PUT, PATCH, DELETE).
 * @param endPoint - The API endpoint.
 * @param data - Data to send in the request (if applicable).
 * @param params - Query parameters for the request (if applicable).
 * @param successCallback - Callback function to execute on successful response.
 * @param errorCallback - Callback function to execute on error response.
 */
const handleApiError = (err: any, errorCallback: Function) => {
  if (err.code === "ERR_NETWORK") {
    errorCallback({
      statusCode: "",
      message: err.message,
    });
  } else if (err.code === "ECONNABORTED") {
    errorCallback({
      data: {
        statusCode: 408,
      },
    });
  } else if (checkUserValidation(err.response?.data)) {
    removeSession();
    window.location.href = "/";
    errorCallback(err.response?.data);
  } else if (
    err.response?.status === apiErrorCode.SESSION_EXPIRED &&
    err.response?.data?.statusCode === apiErrorCode.SESSION_EXPIRED
  ) {
    removeSession();
    window.location.href = "/";
  } else if (
    err.response?.status === apiErrorCode.NOT_AUTHORIZED &&
    err.response?.data?.statusCode === apiErrorCode.NOT_AUTHORIZED
  ) {
    removeSession();
    showAlert(
      2,
      "You have been deactivated/deleted, please contact the Databis Admin",
    );
    setTimeout(() => window.location.replace("/"), 1000);
  } else if (err.response?.status === apiErrorCode.DATA_NOT_FOUND) {
    window.location.replace(Paths.DETAIL_NOT_FOUND);
  } else if (
    err.response?.status === apiErrorCode.ACCESS_UPDATED &&
    err.response?.data?.statusCode === apiErrorCode.ACCESS_UPDATED
  ) {
    window.location.replace("/access-denied");
  } else if (err.response) {
    errorCallback(err.response?.data);
  } else {
    errorCallback({
      data: {
        statusCode: "",
        message: "Please try again later",
      },
    });
  }
};

const getApiCall = (
  endPoint: string,
  params: string,
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios
    .get(API_URL + endPoint + params, {})
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const getApiCallForLogin = (
  endPoint: string,
  params: string,
  successCallback: Function,
  errorCallback: Function,
) => {
  $axios
    .get(API_URL + endPoint + params, {})
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const postApiCall = (
  endPoint: string,
  dataToSend: { [key: string]: any },
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios
    .post(API_URL + endPoint, dataToSend)
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const postApiCallForLogin = (
  endPoint: string,
  dataToSend: { [key: string]: any },
  successCallback: Function,
  errorCallback: Function,
) => {
  $axios
    .post(API_URL + endPoint, dataToSend)
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      console.log(err, "err");
      if (err.code === "ERR_NETWORK") {
        errorCallback({
          statusCode: "",
          message: err.message,
        });
      } else if (err.code === "ECONNABORTED") {
        errorCallback({
          data: {
            statusCode: 408,
          },
        });
      } else if (err.response) {
        errorCallback(err.response?.data);
      } else if (!err.response) {
        errorCallback({
          data: {
            statusCode: "",
            message: "Please try again later",
          },
        });
      }
    });
};

const putApiCall = (
  endPoint: string,
  params: { [key: string]: any },
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios
    .put(API_URL + endPoint, params)
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const patchApiCall = (
  endPoint: string,
  params: { [key: string]: any } | string,
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios
    .patch(API_URL + endPoint, params)
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const deleteApiCall = (
  endPoint: string,
  params: any,
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios
    .delete(API_URL + endPoint + params, {})
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const putS3ApiCall = (
  endPoint: string,
  params: { [key: string]: any },
  successCallback: Function,
  errorCallback: Function,
) => {
  checkToken();
  $axios1
    .put(endPoint, params, { headers: { "Content-Type": params.type } })
    .then((res: any) => {
      successCallback(res);
    })
    .catch((err: any) => {
      handleApiError(err, errorCallback);
    });
};

const getApiCallAsyncAwait = (endPoint: string, params: any) => {
  return $axios.get(API_URL + endPoint, { params });
};

const postApiCallAsyncAwait = (endPoint: string, data: any, params: any) => {
  return $axios.post(API_URL + endPoint, data, { params });
};

const patchApiCallAsyncAwait = (endPoint: string, data: any, params: any) => {
  return $axios.patch(API_URL + endPoint, data, { params });
};

const putApiCallAsyncAwait = (endPoint: string, data: any, params: any) => {
  return $axios.put(API_URL + endPoint, data, { params });
};

export {
  getApiCall,
  postApiCall,
  putApiCall,
  patchApiCall,
  deleteApiCall,
  putS3ApiCall,
  getApiCallAsyncAwait,
  postApiCallAsyncAwait,
  patchApiCallAsyncAwait,
  putApiCallAsyncAwait,
  postApiCallForLogin,
  getApiCallForLogin,
};
